import { SignUp, useAuth } from "@clerk/react";
import { Link } from "@tanstack/react-router";
import {
	Check,
	CreditCard,
	Loader2,
	ShieldCheck,
	Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Wordmark } from "@/components/kb/wordmark";
import { Button } from "@/components/ui/button";
import {
	useBillingPortal,
	useCheckout,
	useSubscription,
} from "@/hooks/useSubscription";

const BENEFITS = [
	"Unlimited notification rules",
	"Alerts for every MLB game, as it happens",
	"Cancel anytime — no contracts",
];

function formatDate(value: string | null | undefined) {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return date.toLocaleDateString(undefined, {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

function CheckoutMessage({
	checkoutState,
	active,
}: {
	active: boolean;
	checkoutState: string | null;
}) {
	if (active) {
		return null;
	}

	if (checkoutState === "success") {
		return (
			<div className="flex items-start gap-3 rounded-lg border bg-positive-soft p-3 text-sm text-foreground">
				<Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" />
				<p>
					Payment confirmed. Activating your membership — this usually takes a
					few seconds.
				</p>
			</div>
		);
	}

	if (checkoutState === "cancelled") {
		return (
			<div className="rounded-lg border bg-muted p-3 text-sm text-muted-foreground">
				Checkout was canceled. You were not charged.
			</div>
		);
	}

	return null;
}

function ActiveSubscription() {
	const subscription = useSubscription();
	const billingPortal = useBillingPortal();

	const status = subscription.data?.status;
	const trialEnd = formatDate(subscription.data?.trialEnd);
	const periodEnd = formatDate(subscription.data?.currentPeriodEnd);
	const error =
		billingPortal.error instanceof Error ? billingPortal.error.message : null;

	return (
		<div className="flex min-h-svh flex-col bg-background">
			<header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
				<Link to="/">
					<Wordmark />
				</Link>
				<span className="type-eyebrow text-muted-foreground">Membership</span>
			</header>

			<main className="flex flex-1 items-center justify-center px-5 pb-16">
				<div className="w-full max-w-md rounded-2xl border bg-card p-7 text-center shadow-sm sm:p-8">
					<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-positive-soft text-positive">
						<Check className="size-6" />
					</div>
					<h1 className="type-display mt-5 text-4xl">You&apos;re in.</h1>
					<p className="mt-2 text-muted-foreground">
						{status === "trialing"
							? "Your free month is active."
							: "Your Knowsball membership is active."}
					</p>

					<dl className="mt-6 divide-y rounded-lg border text-left text-sm">
						<div className="flex items-center justify-between gap-4 px-4 py-3">
							<dt className="text-muted-foreground">Status</dt>
							<dd className="font-medium capitalize">
								{status === "trialing" ? "Free trial" : (status ?? "Active")}
							</dd>
						</div>
						{status === "trialing" && trialEnd ? (
							<div className="flex items-center justify-between gap-4 px-4 py-3">
								<dt className="text-muted-foreground">Trial ends</dt>
								<dd className="font-medium">{trialEnd}</dd>
							</div>
						) : null}
						{periodEnd ? (
							<div className="flex items-center justify-between gap-4 px-4 py-3">
								<dt className="text-muted-foreground">
									{subscription.data?.cancelAtPeriodEnd
										? "Access ends"
										: "Renews"}
								</dt>
								<dd className="font-medium">{periodEnd}</dd>
							</div>
						) : null}
						<div className="flex items-center justify-between gap-4 px-4 py-3">
							<dt className="text-muted-foreground">Price</dt>
							<dd className="font-medium">$7 / month</dd>
						</div>
					</dl>

					{error ? (
						<p className="mt-4 text-sm text-destructive">{error}</p>
					) : null}

					<div className="mt-6 flex flex-col gap-2">
						<Button asChild size="lg">
							<Link to="/">Back to Knowsball</Link>
						</Button>
						{subscription.data?.hasCustomer ? (
							<Button
								disabled={billingPortal.isPending}
								onClick={() => billingPortal.mutate()}
								size="lg"
								variant="outline"
							>
								{billingPortal.isPending ? (
									<Loader2 className="animate-spin" />
								) : (
									<CreditCard />
								)}
								Manage billing
							</Button>
						) : null}
					</div>
				</div>
			</main>
		</div>
	);
}

export function SubscribeScreen() {
	const { isSignedIn } = useAuth();
	const subscription = useSubscription();
	const checkout = useCheckout();
	const [checkoutState] = useState(
		() => new URLSearchParams(window.location.search).get("checkout") ?? null,
	);

	const active = subscription.active;
	const trialDays = subscription.data?.trialDays ?? 30;
	const trialLabel =
		trialDays <= 0
			? null
			: trialDays === 30
				? "First month free"
				: `First ${trialDays} day${trialDays === 1 ? "" : "s"} free`;
	const error = checkout.error instanceof Error ? checkout.error.message : null;

	useEffect(() => {
		if (checkoutState !== "success" || active) {
			return;
		}

		const interval = window.setInterval(() => {
			void subscription.refetch();
		}, 3_000);

		return () => window.clearInterval(interval);
	}, [active, checkoutState, subscription.refetch]);

	if (active) {
		return <ActiveSubscription />;
	}

	return (
		<div className="min-h-svh bg-background">
			<header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
				<Link to="/">
					<Wordmark />
				</Link>
				<span className="type-eyebrow text-muted-foreground">Membership</span>
			</header>

			<main className="mx-auto grid w-full max-w-6xl gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start lg:gap-16 lg:pt-8">
				<section>
					<p className="type-eyebrow text-signal-ink">Knowsball membership</p>
					<h1 className="type-display mt-3 text-5xl leading-[0.95] sm:text-6xl">
						Never miss the moment.
					</h1>
					<p className="mt-5 max-w-md text-base text-muted-foreground">
						Turn any baseball moment into an alert — a player chasing history, a
						team mounting a comeback, a game that will not quit. Build your
						rules once and Knowsball does the watching.
					</p>

					<ul className="mt-8 flex flex-col gap-3">
						{BENEFITS.map((benefit) => (
							<li className="flex items-center gap-3 text-sm" key={benefit}>
								<span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-signal-soft text-signal-ink">
									<Check className="size-3.5" />
								</span>
								{benefit}
							</li>
						))}
					</ul>
				</section>

				<section className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
					<div className="flex items-end gap-2">
						<span className="type-display text-5xl leading-none">$7</span>
						<span className="mb-1 text-sm text-muted-foreground">/ month</span>
					</div>
					{trialLabel ? (
						<div className="mt-3 inline-flex items-center gap-2 rounded-full bg-signal-soft px-3 py-1 text-sm font-medium text-signal-ink">
							<Sparkles className="size-4" />
							{trialLabel}
						</div>
					) : null}
					<p className="mt-4 text-sm text-muted-foreground">
						{trialDays > 0
							? `No charge today. After your free ${trialDays === 30 ? "month" : `${trialDays}-day trial`}, pay $7/month. Cancel anytime.`
							: "Billed $7/month. Cancel anytime."}
					</p>

					<div className="mt-6">
						<CheckoutMessage active={active} checkoutState={checkoutState} />
					</div>

					{isSignedIn ? (
						<div className="mt-4 flex flex-col gap-3">
							<Button
								disabled={checkout.isPending}
								onClick={() => checkout.mutate()}
								size="lg"
							>
								{checkout.isPending ? (
									<Loader2 className="animate-spin" />
								) : (
									<Sparkles />
								)}
								{trialDays > 0
									? "Start your free month"
									: "Subscribe for $7/month"}
							</Button>
							{error ? (
								<p className="text-sm text-destructive">{error}</p>
							) : null}
						</div>
					) : (
						<div className="mt-5 border-t pt-5">
							<p className="mb-4 text-sm text-muted-foreground">
								Create your account to start your free month. Already have one?
								Use the sign-in link below.
							</p>
							<div className="flex justify-center">
								<SignUp routing="hash" />
							</div>
						</div>
					)}

					<p className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
						<ShieldCheck className="size-4" />
						Secure subscription checkout by Stripe.
					</p>
				</section>
			</main>
		</div>
	);
}
